import type { ApiResponse } from '@fiesta/shared';

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:4000';

// Abort a request that stalls this long — flaky 4G/Safari connections routinely
// open a socket that never resolves. Without this a hung fetch hangs forever.
const REQUEST_TIMEOUT_MS = 15000;
// Backoff before each retry of an idempotent GET. Two attempts after the first:
// one quick (a blip), one slower (a cold-started container waking up).
const RETRY_BACKOFF_MS = [400, 1400];

const delay = (ms: number) => new Promise((r) => setTimeout(r, ms));

export class ApiError extends Error {
  readonly code?: string;
  readonly status: number;
  constructor(status: number, message: string, code?: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

// Thin fetch wrapper. `credentials: 'include'` so httpOnly auth + guest-token
// cookies ride along (§11.1, §11.4) — the web app never holds raw tokens in JS.
// GETs are idempotent, so we time them out and retry on network drops / timeouts
// / transient 5xx (cold starts) with backoff — the live gallery must survive a
// spotty party connection without a manual reload. Writes are never retried.
async function request<T>(path: string, init?: RequestInit, attempt = 0): Promise<T> {
  const isGet = !init?.method || init.method === 'GET';
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  let res: Response;
  try {
    res = await fetch(`${BASE_URL}${path}`, {
      ...init,
      credentials: 'include',
      signal: controller.signal,
      headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
    });
  } catch {
    // Aborted (timeout) or network failure. Retry idempotent GETs with backoff.
    const backoff = RETRY_BACKOFF_MS[attempt];
    if (isGet && backoff !== undefined) {
      await delay(backoff);
      return request<T>(path, init, attempt + 1);
    }
    throw new ApiError(0, 'Network error — check your connection and try again', 'NETWORK');
  } finally {
    clearTimeout(timer);
  }

  // Transient server error (502/503/504 from a waking/overloaded container) —
  // retry GETs. A deterministic 500 (real bug) is NOT retried, so we don't
  // triple load during an incident.
  if (isGet && (res.status === 502 || res.status === 503 || res.status === 504)) {
    const backoff = RETRY_BACKOFF_MS[attempt];
    if (backoff !== undefined) {
      await delay(backoff);
      return request<T>(path, init, attempt + 1);
    }
  }

  let body: ApiResponse<T> | undefined;
  try {
    body = (await res.json()) as ApiResponse<T>;
  } catch {
    /* non-JSON response */
  }

  if (!res.ok || !body?.success) {
    throw new ApiError(res.status, body?.error ?? `Request failed (${res.status})`, body?.code);
  }
  return body.data as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, data?: unknown) =>
    request<T>(path, { method: 'POST', body: data ? JSON.stringify(data) : undefined }),
  patch: <T>(path: string, data?: unknown) =>
    request<T>(path, { method: 'PATCH', body: data ? JSON.stringify(data) : undefined }),
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};
